/**
 * As PEÇAS do formulário passo a passo — 16/09/2026.
 *
 * O desenho é o de imóveis (aprovado em 16/09, classes `imv-` em
 * `estilos/imoveis-2026.css`). ⚠️ **Este é o lugar comum que o estudo pedia**
 * (`docs/FORMULARIOS-E-LINK-DO-CLIENTE.md`, A.3): o segundo formulário no
 * desenho novo — a conferência do cadastro — nasce usando estas peças, e não
 * uma cópia — e o **Novo cliente** (17/09/2026) e a **Nova proposta** (18/09/2026) também. ⏭️ O tópico Imóveis ainda tem as dele por dentro; passar a usar
 * estas é o primeiro passo da migração dos formulários (A.4), com a prova de
 * tela igual pixel a pixel.
 */
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { Icone } from './Icone.tsx';
import type { NivelServico } from '../lib/api.ts';
import { NIVEIS } from '../lib/rotulos.ts';
import '../estilos/imoveis-2026.css';
import '../estilos/cadastro-link.css';
import '../estilos/passo-a-passo-2026.css';

/** `lido` = veio de consulta (barra verde) · `veio` = o cliente escreveu (barra roxa). */
export type MarcaDoCampo = 'lido' | 'veio' | undefined;

export function CampoDoPasso({
  rotulo,
  obrigatorio,
  col,
  marca,
  mono,
  nota,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  col: 1 | 2 | 3 | 4 | 6;
  marca?: MarcaDoCampo;
  mono?: boolean;
  nota?: string;
  children: ReactNode;
}) {
  return (
    <label className={`imv-campo c${col}`}>
      <span className="imv-rotulo">
        {rotulo}
        {obrigatorio && <i className="imv-req" title="obrigatório" />}
      </span>
      <span className={`imv-in${mono ? ' mono' : ''}${marca ? ` ${marca}` : ''}`}>{children}</span>
      {nota && <small>{nota}</small>}
    </label>
  );
}

/** `faixa`: nos passos com tabela, a ajuda vai numa faixa acima e a tabela ganha a largura inteira (como em imóveis). */
export function AjudaDoPasso({ titulo, faixa, children }: { titulo: string; faixa?: boolean; children: ReactNode }) {
  return (
    <aside className={`imv-ajuda${faixa ? ' faixa' : ''}`}>
      <span className="imv-rot ouro">{titulo}</span>
      {children}
    </aside>
  );
}

export function PassosDoTopo({
  passos,
  atual,
  alcancado,
  aoIr,
}: {
  passos: readonly string[];
  atual: number;
  /** Até onde a pessoa já foi: dali para trás, dá para voltar clicando. */
  alcancado: number;
  aoIr: (i: number) => void;
}) {
  /* `--passos` só é lido por quem tem menos de seis (a proposta, 18/09/2026):
     a régua de seis de imóveis e do cliente continua a mesma. */
  return (
    <ol className="imv-passos" style={{ '--passos': passos.length } as CSSProperties}>
      {passos.map((t, i) => (
        <li key={t} className={i === atual ? 'at' : i < atual ? 'feito' : i <= alcancado ? 'visto' : undefined}>
          <button type="button" onClick={() => aoIr(i)} disabled={i > alcancado} aria-current={i === atual ? 'step' : undefined}>
            <span className="imv-n">{i < atual ? <Icone nome="check" tamanho={13} /> : i + 1}</span>
            {t}
          </button>
        </li>
      ))}
    </ol>
  );
}

export function Progresso({ atual, total }: { atual: number; total: number }) {
  return (
    <span className="imv-prog">
      <span className="imv-prog-barra">
        <i style={{ width: `${((atual + 1) / total) * 100}%` }} />
      </span>
      passo {atual + 1} de {total}
    </span>
  );
}

/** As 27 UFs — o campo UF do passo a passo é sempre uma escolha, nunca texto livre. */
export const UFS = 'AC AL AM AP BA CE DF ES GO MA MG MS MT PA PB PE PI PR RJ RN RO RR RS SC SE SP TO'.split(' ');

export const soDigitos = (v: string) => v.replace(/\D/g, '');
export const mascaraCep = (v: string) => soDigitos(v).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');

/** Um bloco do passo "Conferir e gravar": o que foi preenchido, e "Corrigir" leva de volta ao passo. */
export function ResumoDoPasso({ titulo, linhas, aoCorrigir }: { titulo: string; linhas: Array<[string, ReactNode]>; aoCorrigir: () => void }) {
  return (
    <section className="cad-bloco">
      <header>
        <h6>{titulo}</h6>
        <button type="button" className="botao-texto s-botao s-botao-p" onClick={aoCorrigir}>
          Corrigir
        </button>
      </header>
      <dl>
        {linhas.map(([r, v]) => (
          <div key={r}>
            <dt>{r}</dt>
            <dd className={v ? undefined : 'falta'}>{v || 'não informado'}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * A escolha do nível, cada botão na cor dele — a Nova proposta (por pesquisa) e
 * o caso (18/09/2026). Os quatro, na ordem da casa; o Customizado não é um
 * quarto nível, e por isso vai no púrpura institucional.
 */
export function EscolhaDoNivel({ nivel, rotulo, aoEscolher }: { nivel: NivelServico; rotulo: string; aoEscolher: (n: NivelServico) => void }) {
  return (
    <div className="imv-seg pap-niveis" role="radiogroup" aria-label={rotulo}>
      {(Object.keys(NIVEIS) as NivelServico[]).map((n) => (
        <button key={n} type="button" role="radio" aria-checked={nivel === n} className={`imv-op pap-op pap-${n}${nivel === n ? ' on' : ''}`} onClick={() => aoEscolher(n)}>
          <i />
          {NIVEIS[n].longo}
        </button>
      ))}
    </div>
  );
}

/**
 * ⭐ **A JANELA EM BLOCOS — 18/09/2026.** A moldura do passo a passo sem os
 * passos, para as janelas de um assunto só (compromisso, reunião, quem entra no
 * Hub): o mesmo topo, o mesmo pé, os campos de `CampoDoPasso`. Uma moldura só,
 * para as dez janelas curtas não divergirem na primeira correção.
 */
export function JanelaEmBlocos({
  rotulo,
  sobrelinha,
  titulo,
  sub,
  largura = 'estreita',
  aoFechar,
  erro,
  rodape,
  children,
}: {
  rotulo: string;
  sobrelinha: ReactNode;
  titulo: ReactNode;
  sub?: ReactNode;
  largura?: 'estreita' | 'media';
  aoFechar: () => void;
  erro?: string | null;
  rodape: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className={`imv cad-conferir pap-janela pap-${largura}`} role="dialog" aria-modal="true" aria-label={rotulo}>
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">{sobrelinha}</span>
              <h3 className="cad-titulo">{titulo}</h3>
              {sub && <p className="cad-sub pap-sub">{sub}</p>}
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={aoFechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <div className="pap-janela-corpo">{children}</div>
          {erro && <p className="erro imv-erro">{erro}</p>}
          <footer className="imv-ficha-pe">{rodape}</footer>
        </section>
      </div>
    </div>
  );
}

/**
 * ⭐ **O BLOCO DE LANÇAR — 18/09/2026.** As linhas que lançam dentro da página
 * (dado da qualificação, vínculo, passivo, gravame, investigado, contato) eram
 * uma fileira de campos sem rótulo, com o nome do campo escondido no exemplo.
 * Agora cada campo diz o que é (`CampoDoPasso`), dentro de um bloco que se
 * distingue da lista — e o lugar e o que o botão faz não mudaram.
 */
export function BlocoDeLancar({
  titulo,
  aoEnviar,
  acoes,
  nota,
  children,
}: {
  titulo?: ReactNode;
  aoEnviar: (e: FormEvent) => void;
  acoes: ReactNode;
  nota?: ReactNode;
  children: ReactNode;
}) {
  return (
    <form className="pap-lancar" onSubmit={aoEnviar} noValidate>
      {titulo && <p className="pap-lancar-titulo">{titulo}</p>}
      <div className="imv-grade-campos">{children}</div>
      {nota && <div className="pap-lancar-nota">{nota}</div>}
      <div className="pap-lancar-acoes">{acoes}</div>
    </form>
  );
}
